
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"

import { AdvancedSearchFilter, CaseDetails, Field, TemplateDetails, CaseField } from "@/types"
import User from "@/types/user"
import { FormattingRule, InsightCalculationTotal, LabelFormat, Insight, InsightCalculation, ColumnOptions, Operator } from "@/types/insights"
import { CaseColumn } from "@/types/caseColumns"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import CaseTable from "@/components/CaseList/CaseTable.vue"
import ColumnFilters from "@/components/Search/ColumnFilters.vue"
import TableCalculations from "./Calculations/TableCalculations.vue"

import { UserSettings } from "@/types/userSettings"

import api from "@/services/api"
import DataCache from "@/caching/caching"

import { filterMixin } from "@/mixins/filtering"
import { CalculationResults, FilteredCalculationResults } from "@/types/insights/calculatedResults"
import { Schedule } from "@/types/schedule"
import { applyFieldChanges } from "@/scripts/caseDetails"
import { formatInsight } from "@/mixins/FormatInsight"
import { TenantFeatures } from "@/types/tenantFeatures"
import CheckStyleFilterModal from "./CheckStyleFilterModal.vue"
import InsightCaseSideBar from "./InsightCaseSideBar.vue"
import { DataType } from "@/types/dataType"
import { SearchClauseParameters } from "@/types/view"
import { CachedItemType } from "@/types/caching/cachedItemType"
import { inlineEditMode } from "@/types/insights/selectiveInlineEdit"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-table": CaseTable,
		"column-filters": ColumnFilters,
		"table-calculations": TableCalculations,
		"schedule-popup": () => import("@/components/Config/Schedules/ScheduleInstance.vue"),
		"check-style-filter-modal": CheckStyleFilterModal,
		"insight-case-side-bar": InsightCaseSideBar
	}
})
export default class TableResults extends Vue {
	/* --- Props --- */

	@Prop()
	resultGroups!: readonly CalculationResults[];

	@Prop()
	checkedItems!:{ [key: string]: string[] }

	@Prop()
	columns!: any[];

	@Prop()
	allCaseFields!: CaseColumn[];

	@Prop({ required: true })
	insight!: Insight

	@Prop()
	calculations: any;

	@Prop()
	calculationGroupByLabels: any;

	@Prop({ default: LabelFormat.Text })
	labelFormat!: LabelFormat;

	@Prop({ default: true })
	showCalculations!: boolean;

	@Prop({ default: false })
	showFilters!: boolean;

	@Prop({ type: Number, default: 50 })
	perPage!: number;

	@Prop({ default: () => [] })
	additionalFilters!: AdvancedSearchFilter[]

	@Prop({ default: false })
	showSelectColumn!:boolean

	@Prop({ type: Boolean, default: false })
	hideRightPad!: boolean;

	@Prop({ default: false })
	editModeEnabled!: boolean

	@Prop({ default: false })
	disableOldInlineEdit!: boolean

	/* --- Vuex --- */

	@State private readonly templateCache!: DataCache<TemplateDetails>

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getMe") public me!: User;
	@Getter("getScreenWidth") public screenWidth!: number;
	@Getter("getUserSettings")

	@setup.State
	fields!: Field[];

	/* --- Constants --- */

	public userSettings!: UserSettings

	/* --- Data --- */

	private filters: AdvancedSearchFilter[] = [];
	filterFunctions: ((record: CaseDetails) => boolean)[] = [];

	private configureCheckFilterColumn: CaseColumn | null = null
	private checkStyleFilterValueSelections: { [columnKey: string]: any[] } = {}
	private checkStyleFilters: { [columnKey: string]: AdvancedSearchFilter } = {}

	private caseToEdit: CaseDetails | null = null
	private loadingCaseToEdit: boolean = false
	private caseToEditTemplate: TemplateDetails | null = null
	private editableColumns: string[] = []
	private updatedCase: CaseDetails | null = null
	private updatedCaseKey: number = 0 // Used to trigger reactivity for 'updatedCase' without changing its value
	private sortedValues: { [key: string]: any }[] = []
	private sortColumn: string | null = null
	private sortAscending: boolean = true
	private disableNextNavigation: boolean = false
	private disablePreviousNavigation: boolean = false

	groupPages: { [key: string]: {
		pageNumber: number,
		pageCount: number
	} } = {};

	cellErrors: { [caseId: string]: {
		columnKeys: string[]
	} } = {}

	private viewScheduleInstance: {
		schedule: Schedule,
		date: Date
	} | null = null

	get tableCalculations () : InsightCalculation[] {
		if (this.calculations) {
			return this.calculations
		}
		return this.insight?.calculations
	}

	get formattingRules () : { [key: string]: FormattingRule[] } {
		return this.insight?.formattingRules || {}
	}

	get columnOptions () : { [key: string]: ColumnOptions[] | any } {
		return this.insight?.columnOptions || {}
	}

	get calculationTotals () : InsightCalculationTotal[] {
		return this.insight?.calculationTotals || []
	}

	get filterColumnGroups () {
		return [
			this.columns
		]
	}

	get filteredResultGroups () : FilteredCalculationResults[] {
		return filterMixin.methods.filterGroups(this.resultGroups, this.filterFunctions, this.labelFormat, this.insight, this.fields)
			.filter(g => g.results && g.results.length > 0)
	}

	get filtered () : boolean {
		return this.filterFunctions && this.filterFunctions.length > 0
	}

	private get shouldDisableOldInlineEdit (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.InlineEditByRow) && this.disableOldInlineEdit
	}

	private get expressionCalculationTotalsFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ExpressionBasedCalculationTotals)
	}

	private get checkFilterSearchFilters (): AdvancedSearchFilter[] {
		return this.insightCheckStyleFiltersEnabled ? Object.values(this.checkStyleFilters).flatMap(f => f) : []
	}

	private get insightCheckStyleFiltersEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.InsightCheckStyleFilters)
	}

	private get checkFilterModalValues (): any[] {
		return this.insightCheckStyleFiltersEnabled && this.configureCheckFilterColumn && this.checkStyleFilterValueSelections[this.configureCheckFilterColumn.key] ? this.checkStyleFilterValueSelections[this.configureCheckFilterColumn.key] as any[] : [] as any[]
	}

	private get isInlineEditSetToNone (): boolean {
		return this.insight?.inlineEditMode === inlineEditMode.None
	}

	private async setCaseToEdit (caseDetails: CaseDetails | null) {
		if (!caseDetails) {
			this.disablePreviousNavigation = true
			this.resetCaseToEdit()
			return
		}

		this.caseToEdit = caseDetails
		if (caseDetails.templateId) {
			const template = this.templateCache.getById(caseDetails.templateId)
			this.caseToEditTemplate = template ?? null
		} else {
			this.caseToEditTemplate = null
		}

		const currentCaseIndex = this.sortedValues.findIndex(
			(caseItem) => caseItem.case?.caseNumber === caseDetails.caseNumber
		)

		if (currentCaseIndex === -1) {
			this.disablePreviousNavigation = true
		} else {
			this.disablePreviousNavigation = currentCaseIndex === 0
			this.disableNextNavigation = currentCaseIndex === this.sortedValues.length - 1
		}
	}

	private resetCaseToEdit () {
		this.caseToEdit = null
		this.caseToEditTemplate = null
		this.editableColumns = []
	}

	mounted () {
		if (this.additionalFilters && this.additionalFilters.length > 0) {
			this.applyFilters()
		} else {
			this.resultGroupsChanged()
		}
	}

	@Watch("filteredResultGroups")
	resultGroupsChanged () {
		const existingGroupPages = this.groupPages
		this.groupPages = {}

		this.filteredResultGroups.forEach((resultGroup, index) => {
			Vue.set(this.groupPages, resultGroup.value, {
				pageNumber: 1,
				pageCount: resultGroup.results ? resultGroup.results.length / 50 : 0
			})
		})

		const allResultsEmpty = this.filteredResultGroups.every(
			resultGroup => !resultGroup.results || resultGroup.results.length === 0
		)
		if (allResultsEmpty) {
			this.resetCaseToEdit()
			this.$emit("set-edit-mode-enabled", false)
		}
	}

	@Watch("additionalFilters", { deep: true })
	additionalFiltersChanged () {
		this.applyFilters()
	}

	@Watch("editModeEnabled")
	onEditModeEnabledChange (newVal: boolean, oldVal: boolean) {
		if (!newVal) {
			this.resetCaseToEdit()
		}
	}

	/**
	 * Get field Label value for field ID
	 */
	public getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	private groupByText (calculation: any, groupCalculationResult: any): string {
		if (calculation.groupByKeyLabels) {
			const calculationKeys = Object.keys(calculation.groupByKeyLabels).filter(k => groupCalculationResult && groupCalculationResult[k] !== undefined)
			return calculationKeys.map(k => `${calculation.groupByKeyLabels[k]}: ${groupCalculationResult[k] !== null && groupCalculationResult[k] !== undefined
				? formatInsight.methods.roundNumber(groupCalculationResult[k], 2)
				: 0}`)
				.join(", ")
		}
		return ""
	}

	private groupTotalsByText (calculationTotal: InsightCalculationTotal, result: any): string {
		let calcList = this.expressionCalculationTotalsFeatureEnabled ? calculationTotal.expressionCalculationList : calculationTotal.calculationList
		if (calcList && calcList.length > 0) {
			const calculation = this.tableCalculations.find((calc: any) => calc.key === calcList![0])
			return calculation ? this.groupByText(calculation, result) : ""
		}
		return ""
	}

	private getCurrentPage (group: any): number {
		if (this.groupPages[group.value] && this.groupPages[group.value].pageNumber) {
			return this.groupPages[group.value].pageNumber
		} else {
			return 1
		}
	}

	private applyFilters () {
		const filters = [
			...this.additionalFilters,
			...this.filters,
			...this.checkFilterSearchFilters
		]

		this.filterFunctions = filterMixin.methods.filtersToFunctions(filters, this.me.id)
		this.resultGroupsChanged()
	}

	forecastClicked (caseItem: { scheduling: any }) {
		if (!caseItem.scheduling) return

		this.viewScheduleInstance = {
			schedule: {
				id: caseItem.scheduling.scheduleId,
				title: caseItem.scheduling.scheduleTitle
			} as Schedule,
			date: new Date(caseItem.scheduling.instanceDate)
		}
	}

	async scheduleInstanceUpdated ({ scheduleId, date, fieldValues }: { scheduleId: string, date: Date, fieldValues: { [ key: string ]: { label: string, value: any }} }) {
		const dateString = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`

		const updateInstance = await api.scheduleInstance(scheduleId, date)

		if (this.resultGroups) {
			this.resultGroups.forEach(group => {
				if (!group.results) return

				group.results.filter(r => r.scheduling && r.scheduling.scheduleId === scheduleId && r.scheduling.instanceDate === dateString)
					.forEach(insightRecord => {
						applyFieldChanges(insightRecord, fieldValues)
						if (updateInstance.updatedScheduleInstance && updateInstance.updatedScheduleInstance.users) {
							insightRecord.users = updateInstance.updatedScheduleInstance.users
						}
					})
			})
		}
	}

	private roundCalculationValue (number: string) {
		return formatInsight.methods.roundNumber(number, 2)
	}

	private async caseEdited () {
		if (this.insight.id) {
			await api.insightMarkAsCacheExpired(this.insight.id)
		}
	}

	public getBulkDelete (groupValue: string, bulkDelete: Set<string>) {
		this.$emit("getBulkDelete", { groupValue, items: [...new Set(bulkDelete)] })
	}

	private getCheckedItems (value:string) {
		return !!this.checkedItems && !!this.checkedItems[value] ? this.checkedItems[value] : []
	}

	private async openCheckStyleConfigForColumn (column: CaseColumn) {
		if (this.insightCheckStyleFiltersEnabled) {
			Vue.set(this, "configureCheckFilterColumn", column)
			await this.$nextTick()
		}
	}

	private checkFilterValueToSearchFilter (column: CaseColumn, value: any): AdvancedSearchFilter {
		let operator: Operator
		let filterKey: string | undefined = column.key
		let searchClauseParameters: SearchClauseParameters | undefined
		if (column.fieldDataType) {
			switch (column.fieldDataType as DataType) {
				case DataType.boolean:
					operator = value ? Operator.IsSet : Operator.NotSet
					break
				case DataType.string:
					operator = Operator.Contains
					break
				case DataType.array:
					operator = Operator.Equals
					break
				case DataType.arrayMultiple:
					operator = Operator.Contains
					break
				case DataType.userRole:
					operator = Operator.Contains
					break
				case DataType.number:
					operator = Operator.Equals
					if (column.dynamicField) {
						filterKey = undefined
					}
					break
				case DataType.dateTime:
					operator = Operator.OnDay
					break
				case DataType.timeSpentAsValue:
					operator = Operator.Equals
					searchClauseParameters = {
						timeSpentClause: {
							searchLabel: value.searchLabel,
							hoursSpent: value.hoursSpent
						}
					} as SearchClauseParameters
					break
				default:
					operator = Operator.Equals
					break
			}
		} else {
			operator = Operator.Equals
		}

		return {
			key: column.key,
			column: column,
			operator: operator,
			filterKey: filterKey,
			value: value,
			searchClauseParameters: searchClauseParameters
		} as AdvancedSearchFilter
	}

	private async checkStyleFilterForColumnChanged (column: CaseColumn, selectedValues: any[]) {
		if (this.insightCheckStyleFiltersEnabled) {
			Vue.set(this.checkStyleFilterValueSelections, column.key, selectedValues || [])
			await this.$nextTick()
			if (this.checkStyleFilterValueSelections[column.key].length === 0) {
				Vue.delete(this.checkStyleFilterValueSelections, column.key)
				Vue.delete(this.checkStyleFilters, column.key)
			} else {
				const filters = this.checkStyleFilterValueSelections[column.key].map(value => this.checkFilterValueToSearchFilter(column, value))
				const orFilter: AdvancedSearchFilter = {
					key: column.key,
					orFilters: filters
				}
				Vue.set(this.checkStyleFilters, column.key, orFilter)
			}
			this.applyFilters()
		}
	}

	private resetCheckStyleFilterForAllColumns () : void {
		if (this.insightCheckStyleFiltersEnabled) {
			this.checkStyleFilters = {}
			this.checkStyleFilterValueSelections = {}
			this.applyFilters()
		}
	}

	private handleCloseFilterModal () {
		Vue.set(this, "configureCheckFilterColumn", null)
	}

	private async selectNextCaseToEdit () {
		if (!this.sortedValues || !this.caseToEdit) return

		const currentCaseIndex = this.sortedValues.findIndex(
			(caseItem) => caseItem.case.caseNumber === this.caseToEdit!.caseNumber
		)

		if (currentCaseIndex >= 0 && currentCaseIndex < this.sortedValues.length - 1) {
			try {
				this.loadingCaseToEdit = true
				const nextCase = this.sortedValues[currentCaseIndex + 1].case
				const currentPage = Math.floor(currentCaseIndex / this.perPage) + 1
				const nextPage = Math.floor((currentCaseIndex + 1) / this.perPage) + 1

				if (currentPage !== nextPage) {
					const group = this.filteredResultGroups.find((g) =>
						g.results.some((caseItem) => caseItem.caseNumber === this.caseToEdit!.caseNumber)
					)
					if (group && group.value in this.groupPages) {
						this.groupPages[group.value].pageNumber = nextPage
					}
				}
				const caseDetails = await api.caseDetails(nextCase.caseNumber)
				this.setCaseToEdit(caseDetails)
			} catch (err) {
				console.error("Failed to fetch next case details:", err)
			} finally {
				this.loadingCaseToEdit = false
			}
		}
	}

	private async selectPreviousCaseToEdit () {
		if (!this.sortedValues || !this.caseToEdit) return

		const currentCaseIndex = this.sortedValues.findIndex(
			(caseItem) => caseItem.case.caseNumber === this.caseToEdit!.caseNumber
		)

		if (currentCaseIndex > 0) {
			try {
				this.loadingCaseToEdit = true
				const previousCase = this.sortedValues[currentCaseIndex - 1].case
				const currentPage = Math.floor(currentCaseIndex / this.perPage) + 1
				const previousPage = Math.floor((currentCaseIndex - 1) / this.perPage) + 1

				if (currentPage !== previousPage) {
					const group = this.filteredResultGroups.find((g) =>
						g.results.some((caseItem) => caseItem.caseNumber === this.caseToEdit!.caseNumber)
					)
					if (group && group.value in this.groupPages) {
						this.groupPages[group.value].pageNumber = previousPage
					}
				}
				const caseDetails = await api.caseDetails(previousCase.caseNumber)
				this.setCaseToEdit(caseDetails)
			} catch (err) {
				console.error("Failed to fetch previous case details:", err)
			} finally {
				this.loadingCaseToEdit = false
			}
		}
	}

	private handleEditableFieldIdsChanged (newEditableFieldIds: string[]) {
		Vue.set(this, "editableColumns", newEditableFieldIds)
	}

	private handleCaseUpdated (updatedCase: CaseDetails, updatedCaseField: CaseField | null) {
		// Update any cell errors that could have occured with calculation columns
		if (updatedCaseField && Array.isArray((this.insight as any).calculationColumns)) {
			const affectedColumns: string [] = [];
			(this.insight as any).calculationColumns.forEach((column: any) => {
				if (column.expression.includes(updatedCaseField.field.id)) {
					affectedColumns.push(column.key)
				}
			})
			if (affectedColumns.length > 0 && updatedCase.id) {
				if (!this.cellErrors[updatedCase.id]) {
					this.cellErrors[updatedCase.id] = { columnKeys: [] }
				}

				this.cellErrors[updatedCase.id].columnKeys.push(...affectedColumns)
				// Remove duplicates
				this.cellErrors[updatedCase.id].columnKeys = [...new Set(this.cellErrors[updatedCase.id].columnKeys)]
			}
		}
		this.updatedCase = updatedCase
		this.updatedCaseKey++
	}

	private async handleSortedValuesUpdated (sorted: { [key: string]: any }[], sortColumn: string | null, sortAscending: boolean, rowClicked: boolean = false) {
		if (!this.caseToEdit) return

		// Handle filter updates
		const isFiltered = sorted.length !== this.sortedValues.length && !rowClicked
		if (isFiltered) {
			this.sortedValues = sorted
			if (this.filteredResultGroups.length > 0 && this.filteredResultGroups[0].results.length > 0) {
				const sortedFilteredCase = this.sortedValues.find(sortedCase =>
					this.filteredResultGroups.some(group =>
						group.results.some(caseItem => caseItem.caseNumber === sortedCase.case.caseNumber)
					)
				)

				if (sortedFilteredCase) {
					try {
						this.loadingCaseToEdit = true
						const caseDetails = await api.caseDetails(sortedFilteredCase.case.caseNumber)
						this.setCaseToEdit(caseDetails)
					} catch (err) {
						console.error("Failed to fetch first case details for edit:", err)
					} finally {
						this.loadingCaseToEdit = false
					}
				} else {
					this.resetCaseToEdit()
				}
			} else {
				this.resetCaseToEdit()
			}
			return
		}

		// Handle sorting updates
		const currentCaseIndex = sorted.findIndex(
			(caseItem) => caseItem.case?.caseNumber === this.caseToEdit!.caseNumber
		)
		const containsCaseToEdit = currentCaseIndex !== -1
		this.sortedValues = sorted
		if (containsCaseToEdit) {
			const group = this.filteredResultGroups.find((g) =>
				g.results.some((caseItem) => caseItem.caseNumber === this.caseToEdit!.caseNumber)
			)
			if (this.sortColumn !== sortColumn || this.sortAscending !== sortAscending) {
				if (group && group.value in this.groupPages) {
					const currentPage = this.groupPages[group.value].pageNumber || 1
					const startIndex = (currentPage - 1) * this.perPage
					const firstCase = sorted[startIndex].case
					if (firstCase) {
						try {
							this.loadingCaseToEdit = true
							const caseDetails = await api.caseDetails(firstCase.caseNumber)
							this.setCaseToEdit(caseDetails)
						} catch (err) {
							console.error("Failed to fetch next case details:", err)
						} finally {
							this.loadingCaseToEdit = false
						}
					}
				}
			}
			this.sortColumn = sortColumn
			this.sortAscending = sortAscending
		}
	}
}

